import React, { useEffect, useState, useRef, useMemo } from 'react';
import SourceFlowImage from "@sourceflow-uk/sourceflowimage"
import SourceFlowText from "@sourceflow-uk/sourceflowtext"
import {
    Container,
    Row,
    Col,
    Card,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    UncontrolledDropdown
} from 'reactstrap';
import Link from "next/link"
import styles from './styles.module.scss';
import NewsTag from "../../.sourceflow/news_tag.json";
import NewsContentType from "../../.sourceflow/news_content_type.json";
import SearchIcon from '/public/site-assets/svg/black-magnifying-glass.svg';
import { useRouter } from 'next/router';

// One dropdown per collection: news_tag holds the sector and audience tags, news_content_type
// the post formats. Entries without a slug have no tag page to link to.
const toOptions = (collection) =>
    (collection?.category_values ?? [])
        .filter((entry) => entry.title?.en && entry.url_slug)
        .map((entry) => ({ title: entry.title.en, slug: entry.url_slug }))
        .sort((a, b) => a.title.localeCompare(b.title));

const categoryOptions = toOptions(NewsTag);
const contentTypeOptions = toOptions(NewsContentType);

// `tag` comes through as a plain array on flattened items, but as { en: [...] } on raw ones
const getTags = (post) => {
    const tags = Array.isArray(post?.tag) ? post.tag : post?.tag?.en;
    return Array.isArray(tags) ? tags : tags ? [tags] : [];
};

export default function AllBlogs({
    global,
    pathPrefix,
    customClass = "",
    post,
    pagePrefix = "blog/",
    tagName,
    ParentNews = false,
    locale,
    cardPagePrefix = "blog/",
    enableFilterBy = true,

    heading

}) {
    const router = useRouter();

    const [searchValue, setSearchValue] = useState('');
    const [visiblePosts, setVisiblePosts] = useState(4); // Number of posts initially visible
    const [showMore, setShowMore] = useState(true);

    const localePrefix = `/${locale ? `${locale}` : ""}`;
    const showAllHref = `${localePrefix}${pagePrefix.replace(/\/$/, "")}`;

    // Both dropdowns navigate to the tag's own page, with two exceptions: the "blog" slug is the
    // listing index itself, and on the UK site Career Advice is a hand-built page at /career-advice
    // rather than a tag page (the US site has no equivalent, so it uses its tag page as normal).
    const isUSSite = pagePrefix.startsWith("us/");
    const categoryHref = ({ title, slug }) =>
        title === "Career Advice" && !isUSSite
            ? `${localePrefix}career-advice`
            : slug === "blog"
            ? showAllHref
            : `${localePrefix}${pagePrefix}${slug}`;

    const handleSearchChange = (event) => {
        setSearchValue(event.target.value);
    };

    const filteredPosts = useMemo(() => {
        const lowerCaseKeyword = searchValue.toLowerCase();
        return (post ?? []).filter((post) => {
            const tags = getTags(post);
            return (
                !lowerCaseKeyword ||
                post.title?.toLowerCase().includes(lowerCaseKeyword) ||
                tags.some((tag) => tag?.toLowerCase().includes(lowerCaseKeyword))
            );
        });
    }, [post, searchValue]);

    const loadMore = () => {
        setVisiblePosts(visiblePosts + 4); // Increase the number of visible posts by 4
        if (visiblePosts + 4 >= filteredPosts.length) {
            setShowMore(false); // Disable the "Show More" button when there are no more posts to show
        }
    };

    useEffect(() => {
        // Reset the visible count whenever the keyword search changes
        setVisiblePosts(8);
        setShowMore(filteredPosts.length > 8);
    }, [filteredPosts]);


    useEffect(() => {
        // Listen for route changes and reset the component's state
        setSearchValue('');
    }, [router.asPath]); // Listen for route changes


    return (
        <section className={`${styles.root} ${customClass}`} >
            <Container>
                <div className="filters d-flex flex-wrap flex-md-nowrap justify-content-between mb-4">
                    <div className='mb-4 mb-lg-0'>
                        <h2>
                            <SourceFlowText global={global} path={`${pathPrefix}.heading`}>
                                {heading}
                            </SourceFlowText>
                        </h2>
                    </div>
                    <div>
                        {enableFilterBy ?
                            <div className={`d-flex align-items-center gap-4 flex-wrap`}>
                                <div className='d-flex flex-wrap align-items-center gap-3'>
                                    <label className='me-4'>Filter by</label>
                                    <UncontrolledDropdown>
                                        <DropdownToggle>{categoryOptions.some((category) => category.title === tagName) ? tagName : "Category / topic"}</DropdownToggle>
                                        <DropdownMenu>
                                            <Link href={showAllHref} passHref>
                                                <DropdownItem>All</DropdownItem>
                                            </Link>
                                            {categoryOptions.map((category, index) => (
                                                <Link key={index} href={categoryHref(category)} passHref>
                                                    <DropdownItem active={category.title === tagName}>{category.title}</DropdownItem>
                                                </Link>
                                            ))}
                                        </DropdownMenu>
                                    </UncontrolledDropdown>
                                    <UncontrolledDropdown>
                                        <DropdownToggle>{contentTypeOptions.some((category) => category.title === tagName) ? tagName : "Content type"}</DropdownToggle>
                                        <DropdownMenu>
                                            <Link href={showAllHref} passHref>
                                                <DropdownItem>All</DropdownItem>
                                            </Link>
                                            {contentTypeOptions.map((category, index) => (
                                                <Link key={index} href={categoryHref(category)} passHref>
                                                    <DropdownItem active={category.title === tagName}>{category.title}</DropdownItem>
                                                </Link>
                                            ))}
                                        </DropdownMenu>
                                    </UncontrolledDropdown>
                                </div>
                                <div className='position-relative search'>
                                    <input
                                        type="text"
                                        id="search"
                                        placeholder="Keyword search"
                                        onChange={handleSearchChange}
                                        value={searchValue}
                                    />
                                    <span className='search-icon d-inline-block position-absolute'>
                                        <SearchIcon />
                                    </span>
                                </div>
                            </div>
                            : ""}
                    </div>
                </div>

                <Row className="isotope-grid">
                    {filteredPosts.length !== 0 ? (<>
                        {filteredPosts.slice(0, visiblePosts).map((post, index) => (
                            <Col md={3} className={`item ${post.tag?.en?.toLowerCase().replaceAll(",", "")} mb-4`} key={index}>
                                <Card className="overflow-hidden h-100">
                                    <Link href={
                                            post?.external_campaign
                                            ? post.external_campaign
                                            : `${locale ? `/${locale}` : ""}/${cardPagePrefix}${post.url_slug}`
                                        }>
                                        <a className="d-flex flex-wrap justify-content-between h-100" {...(post?.external_campaign ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
                                            <div className="position-relative">
                                                <SourceFlowImage
                                                    src={post?.image ? post.image : "/site-assets/placeholders/img-placeholder.jpg"}
                                                    size="800x500"
                                                    alt={post?.title}
                                                    className="card-img"
                                                />
                                                {/* {post?.tag ?
                                                    <div>
                                                        <span className="tertiary-bg text-black d-inline-block px-2 rounded-3 position-absolute post-tag"><small><strong>{post.tag.length <= 10 ? post.tag : post.tag.substring(0, 40) + "..."}</strong></small></span>
                                                    </div> : null} */}
                                                {enableFilterBy && post?.tag?.length >= 1 && (
                                                    <div className="position-absolute post-tag">
                                                        {post.tag?.map((post, index) => (
                                                            <span className="secondary-bg text-black d-inline-block px-2 rounded-3 me-2 mb-2 text-nowrap" key={index}>
                                                                {post}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="conteWrapper p-3">
                                                <div>
                                                    <p className="post-title"><strong>{post?.title}</strong></p>
                                                    {post?.short_description ?
                                                        <p className="post-description">{post.short_description}</p>
                                                        : ""}
                                                    {post?.salary_package ?
                                                        <p className="post-salary">
                                                            {post?.salary_package}
                                                        </p>
                                                        : ""}
                                                </div>
                                                <span>
                                                    Read More
                                                </span>
                                            </div>
                                        </a>
                                    </Link>
                                </Card>
                            </Col>
                        ))} </>)
                        : (
                            <div><p>No content availabe. Please select another filter.</p></div>
                        )
                    }
                </Row>
                {showMore && (
                    <div className="text-center mt-4">
                        <button className="secondaryBtn no-arrow border-0" onClick={loadMore}>Load more</button>
                    </div>
                )}
            </Container>
        </section>
    )

}